import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { endpoints } from '../api/endpoints';
import { useOptionalAuth } from '../auth/AuthProvider';
import type { TenantThemeDto } from '@oip/contracts';
import { ThemeModeProvider } from './ThemeModeProvider';

/**
 * The tenant's wordmark and logo, for chrome that is not a MUI token.
 *
 * Colours reach components through the theme; the NAME and LOGO cannot, so they
 * travel by context rather than being fetched a second time in the sidebar.
 */
const TenantBrandingContext = createContext<TenantThemeDto | null>(null);

/** Null when unbranded, unentitled, or not yet loaded. */
export function useTenantBranding(): TenantThemeDto | null {
  return useContext(TenantBrandingContext);
}

/**
 * Resolves the tenant's theme and hands it to `ThemeModeProvider`.
 *
 * Split from that provider so theme construction stays free of a QueryClient and
 * an auth context — component tests and stories mount the presentation half alone
 * and should not have to supply either.
 *
 * The entitlement is enforced SERVER-side: `/me/theme` returns platform defaults
 * for a tenant without `WHITE_LABEL` rather than refusing, so revoking the feature
 * returns everyone to defaults on their next load without this component knowing
 * anything about entitlements.
 */
export function TenantThemeProvider({ children }: { children: ReactNode }) {
  const auth = useOptionalAuth();

  const signedIn = auth?.ready === true && auth.tenantId !== null;

  /*
   * BEFORE a tenant is known, the brand comes from the HOSTNAME.
   *
   * This is what makes the product white-label rather than merely themed. The
   * login screen has no tenant, so until this existed every white-label
   * partner's users saw the platform's branding on the first page they ever
   * loaded — the most visible thing a white-label product can get wrong.
   *
   * `window.location.hostname` rather than letting the server read the `Host`
   * header: behind a proxy that header is proxy-supplied, and a route whose
   * behaviour depends on whether a proxy forwards it breaks differently in
   * every environment.
   */
  const publicQuery = useQuery({
    queryKey: ['public-theme', window.location.hostname],
    queryFn: () => endpoints.publicTheme(window.location.hostname),
    // Only while signed OUT. Once a tenant is known its own chain is more
    // specific — a company's name and colour sit inside the partner's frame —
    // and D10 resolves the signed-in brand from the TENANT, never the host, so
    // that following a link to another partner's subdomain cannot re-skin
    // somebody's own data.
    enabled: auth?.ready === true && !signedIn,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  const query = useQuery({
    queryKey: ['tenant-theme'],
    queryFn: () => endpoints.theme(),
    /*
     * Only once a tenant is known. Asking on the login screen would be a
     * guaranteed 401 on every visit — noise that would eventually be mistaken
     * for a real auth problem. That is what `publicQuery` above is for.
     */
    enabled: signedIn,
    // Branding changes at the pace somebody edits a form. Re-fetching per
    // navigation would be a request a page for a value that almost never moves.
    staleTime: 60 * 60 * 1000,
    // A failed theme fetch must never block the application: falling back to
    // platform defaults is a perfectly good outcome, and retrying makes a
    // transient blip into a slow first paint.
    retry: false,
  });

  // The signed-in chain wins; the host brand is what an anonymous visitor gets.
  const theme = query.data ?? publicQuery.data;

  const branding = useMemo(
    () => ({
      accent: theme?.accent ?? null,
      secondaryAccent: theme?.secondaryAccent ?? null,
      fontFamily: theme?.fontFamily ?? null,
      cornerRadius: theme?.cornerRadius ?? null,
      density: theme?.density ?? null,
      navStyle: theme?.navStyle ?? null,
    }),
    // `theme`, not `query.data`. Depending on the signed-in query alone meant
    // the memo never recomputed for an anonymous visitor — the tab title
    // updated (it is not memoised) while the colours stayed the platform's,
    // which looked like the public route not working at all.
    [theme],
  );

  /*
   * The browser tab — the surface a person looks at most in a working day, and
   * unbranded until now. Written directly to the document because there is no
   * React-managed <head> here, and reverted on unmount so a test or a tenant
   * switch cannot leave the previous brand behind.
   */
  const title = theme?.displayName ?? null;
  const favicon = theme?.faviconUrl ?? null;
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const previousTitle = document.title;
    if (title) document.title = title;

    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    const previousHref = link?.href ?? null;
    if (favicon) {
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = favicon;
    }
    return () => {
      document.title = previousTitle;
      if (link && previousHref !== null) link.href = previousHref;
    };
  }, [title, favicon]);

  /*
   * `theme`, not `query.data`: an anonymous visitor on a partner subdomain gets
   * the partner's brand here too, and the login screen reads it for its
   * wordmark. Until this was published, the tab title changed and the page did
   * not — which read as the public route not working at all.
   */
  return (
    <TenantBrandingContext.Provider value={theme ?? null}>
      <ThemeModeProvider
        branding={branding}
        defaultColourMode={theme?.defaultColourMode ?? null}
      >
        {children}
      </ThemeModeProvider>
    </TenantBrandingContext.Provider>
  );
}
